import type { RuntimeSceneExperienceEvent } from "../components/RuntimeSceneExperience";

type RuntimeAuditRecorder = (
  type: "interaction_completed",
  payload: Record<string, string | number | boolean | null>,
  options: { sceneId: string },
) => void;

const assistantEventNames = new Set<RuntimeSceneExperienceEvent["name"]>([
  "assistant_opened",
  "assistant_answered",
  "assistant_refused",
]);

const refusalReasons = new Set([
  "empty_query",
  "query_too_long",
  "prompt_injection",
  "prohibited_request",
  "contains_personal_data",
  "out_of_scene_scope",
  "unapproved_content",
  "no_reliable_match",
]);

function safeRuntimeId(value: string): string {
  return /^[A-Z0-9_-]{1,64}$/u.test(value) ? value : "unknown";
}

function safeSequenceIndex(value: number): number {
  return Number.isInteger(value) && value >= 0 && value <= 1_000 ? value : 0;
}

/**
 * Persists only a fixed assistant-audit envelope. The free-text query and learner fields are
 * intentionally never copied from `event.details`, even if a caller accidentally includes them.
 */
export function persistRuntimeAssistantAuditEvent(
  event: RuntimeSceneExperienceEvent,
  record: RuntimeAuditRecorder,
): boolean {
  if (!assistantEventNames.has(event.name)) return false;

  const payload: Record<string, string | number | boolean | null> = {
    runtimeEvent: event.name,
    sequenceId: safeRuntimeId(event.sequenceId),
    sequenceIndex: safeSequenceIndex(event.sequenceIndex),
  };

  if (event.name === "assistant_opened") {
    payload.mode = "offline_grounded_p0";
  } else if (event.name === "assistant_answered") {
    const count = event.details?.citationEntryCount;
    payload.citationEntryCount =
      typeof count === "number" && Number.isInteger(count) && count >= 0 ? Math.min(count, 100) : 0;
  } else {
    const reason = event.details?.reason;
    payload.reason = typeof reason === "string" && refusalReasons.has(reason) ? reason : "unknown";
  }

  record("interaction_completed", payload, { sceneId: safeRuntimeId(event.sceneId) });
  return true;
}
